import React, { useEffect, useMemo, useState } from 'react';
import aiAutomationApi from '../../utils/aiAutomationApi';
import {
  MODES,
  RECRUITER_ORDERS,
  categorySum,
  recruiterCount,
  jobFromDefaults,
  labelFor,
  normalizeWorkMode,
  validateConfiguration,
} from '../../pages/interview/config/interviewConfigurationModel';
import PriorityTopicsEditor from '../interview/PriorityTopicsEditor';
import { validateTopics } from '../../pages/ai-interview/utils/aiInterviewApi';

const inputCls = (isDark) => `w-full rounded-lg border px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 ${
  isDark ? 'border-gray-600 bg-gray-800 text-white focus:ring-indigo-900/40' : 'border-gray-300 bg-white text-gray-900 focus:ring-indigo-100'
}`;

const panelCls = (isDark) => `rounded-lg border p-4 ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`;

const defaultQuestion = (category = 'technical') => ({
  question_text: '',
  category,
  expected_skills: [],
  weightage: 1,
});

const mapConfig = (config, defaults, sourceSessionId) => ({
  ...jobFromDefaults(defaults, sourceSessionId),
  ...(config || {}),
  session_id: sourceSessionId,
  interview_id: config?.interview_id || sourceSessionId,
  categories: config?.categories || [],
  custom_questions: config?.custom_questions || [],
  scoring_config: config?.scoring_config || {},
  threshold_config: config?.threshold_config || {},
});

const asList = (value) => Array.isArray(value) ? value.filter(Boolean) : [];

const formatCompetencies = (rows = []) => asList(rows)
  .map((item) => {
    if (!item || typeof item !== 'object') return String(item || '').trim();
    const label = item.label || item.key || '';
    return item.weight ? `${label} (${item.weight}%)` : label;
  })
  .filter(Boolean)
  .join(', ');

const formatAccommodations = (acc) => {
  if (!acc || !acc.enabled) return '';
  return asList(acc.selected_options).map((item) => item.label || item.key).filter(Boolean).join(', ');
};

const SummaryGrid = ({ cfg, isDark }) => {
  const sd = cfg?.setup_details || {};
  const thresholds = cfg?.threshold_config || {};
  const task = sd.practical_task || {};
  const rows = [
    ['Modes', asList(sd.interview_modes).join(', ')],
    ['Competencies', formatCompetencies(sd.required_competencies)],
    ['Mandatory criteria', asList(sd.mandatory_criteria).join(', ')],
    ['Disqualifiers', asList(sd.disqualifying_criteria).join(', ')],
    ['Thresholds', thresholds.select || thresholds.maybe ? `Recommend >= ${thresholds.select ?? 70}, Review >= ${thresholds.maybe ?? 50}${thresholds.source ? ` (${thresholds.source})` : ''}` : 'Company default'],
    ['Question types', asList(sd.preferred_question_types || sd.question_types).join(', ')],
    ['Accommodations', formatAccommodations(sd.interview_accommodations)],
    ['Allocation', asList(sd.question_allocation).map((row) => `${row.label || row.section}: ${row.count}`).join(', ')],
    ['Practical task', sd.practical_task_required || sd.coding_or_task_required ? `${task.title || 'Included'}${sd.practical_task_minutes ? ` (${sd.practical_task_minutes} min)` : ''}` : 'Not included'],
  ].filter(([, value]) => value);
  if (!rows.length) return null;
  return (
    <div className="mt-4 grid gap-2 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label} className={`rounded-lg p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
          <p className={`mt-1 text-xs leading-5 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{value}</p>
        </div>
      ))}
    </div>
  );
};

const AIInterviewConfigSelector = ({ companyId, sourceSessionId, value, onChange, isDark, primaryColor,
  locked = false, onRequestNew, forceNew = false }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [cfg, setCfg] = useState(jobFromDefaults(null, sourceSessionId));
  const [selectedConfig, setSelectedConfig] = useState(value || null);
  const [editing, setEditing] = useState(!value?.id);

  const isAI = cfg.mode === 'ai';
  const isRecruiter = cfg.mode === 'recruiter';
  const isHybrid = cfg.mode === 'hybrid';
  const aiQuestionTarget = Math.max(0, Number(cfg.total_questions || 0) - recruiterCount(cfg));
  const errors = useMemo(() => validateConfiguration(cfg, 'job'), [cfg]);
  const categoryTotal = categorySum(cfg);

  const patchCfg = (patch) => setCfg((prev) => ({ ...prev, ...patch }));
  const patchDetails = (patch) => setCfg((prev) => ({
    ...prev,
    setup_details: { ...(prev.setup_details || {}), ...patch },
  }));

  useEffect(() => {
    setSelectedConfig(value || null);
    if (value?.id) setEditing(false);
  }, [value]);

  const loadSetup = async () => {
    if (!companyId || !sourceSessionId) return;
    setLoading(true);
    setError('');
    try {
      const defaultsData = await aiAutomationApi.get(`/ai-interview/config/defaults?company_id=${encodeURIComponent(companyId)}`);
      const baseDefaults = defaultsData?.defaults || {};
      const categoryMeta = defaultsData?.categories || [];
      setCategories(categoryMeta);

      const listData = await aiAutomationApi.get(`/ai-interview/config/list?company_id=${encodeURIComponent(companyId)}`);
      const list = listData?.configurations || [];
      const existing = list.find((item) => (
        String(item.company_id || '') === String(companyId || '')
        && String(item.session_id || '') === String(sourceSessionId || '')
      ));

      if (!forceNew && existing?.id) {
        const raw = await aiAutomationApi.get(`/ai-interview/config?company_id=${encodeURIComponent(companyId)}&session_id=${encodeURIComponent(sourceSessionId)}&raw=true`);
        const configuration = raw?.configuration || existing;
        const mapped = mapConfig(configuration, baseDefaults, sourceSessionId);
        setCfg(mapped);
        setSelectedConfig(configuration);
        onChange(configuration);
        setEditing(false);
      } else {
        const fresh = jobFromDefaults(baseDefaults, sourceSessionId);
        fresh.name = fresh.name || 'AI interview setup 1';
        setCfg(fresh);
        setSelectedConfig(null);
        onChange(null);
        setEditing(true);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load AI interview setup.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, sourceSessionId, forceNew]);

  const setMode = (mode) => {
    patchCfg({
      mode,
      question_count_mode: mode === 'recruiter' ? 'fixed' : cfg.question_count_mode || 'fixed',
      custom_questions: mode === 'ai' ? [] : (cfg.custom_questions?.length ? cfg.custom_questions : [defaultQuestion(categories[0]?.name)]),
    });
  };

  const addCategory = () => {
    const name = categories[0]?.name || 'technical';
    patchCfg({ categories: [...(cfg.categories || []), { category_name: name, question_count: 1 }] });
  };

  const updateCategory = (index, patch) => {
    patchCfg({ categories: (cfg.categories || []).map((item, idx) => (idx === index ? { ...item, ...patch } : item)) });
  };

  const addQuestion = () => {
    patchCfg({ custom_questions: [...(cfg.custom_questions || []), defaultQuestion(categories[0]?.name)] });
  };

  const updateQuestion = (index, patch) => {
    patchCfg({ custom_questions: (cfg.custom_questions || []).map((item, idx) => (idx === index ? { ...item, ...patch } : item)) });
  };

  const save = async () => {
    if (locked) {
      // Backend enforces this with a 409; guard here so the recruiter gets a clear message, not an error.
      setError('This setup is locked because candidates have already been invited. Create a new setup to make changes.');
      return;
    }
    const validation = validateConfiguration(cfg, 'job');
    if (validation.length) {
      setError(validation[0]);
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        ...cfg,
        session_id: String(sourceSessionId || ''),
        interview_id: String(cfg.interview_id || sourceSessionId || ''),
        scoring_config: cfg.scoring_config || {},
        threshold_config: cfg.threshold_config || {},
      };
      // forceNew ("Create New Setup") mints a fresh, independent config identity so it never overwrites
      // an existing (possibly locked) setup's config on this job.
      const forceQs = forceNew ? '&force_new=true' : '';
      const data = await aiAutomationApi.post(`/ai-interview/config?company_id=${encodeURIComponent(companyId)}${forceQs}`, payload);
      const configuration = data?.configuration || payload;
      setSelectedConfig(configuration);
      onChange(configuration);
      setEditing(false);
      setMessage('AI interview setup saved.');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.detail || err.message || 'Failed to save AI interview setup.');
    } finally {
      setSaving(false);
    }
  };

  if (!sourceSessionId) {
    return (
      <div className={panelCls(isDark)}>
        <p className="text-sm text-red-500">Session context is missing. Please reopen the job and try again.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`rounded-lg p-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading AI interview setup...</p>
      </div>
    );
  }

  if (!editing && selectedConfig?.id) {
    return (
      <div className={panelCls(isDark)}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedConfig.name || cfg.name || 'AI interview setup'}</p>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {(selectedConfig.mode || cfg.mode || 'ai').toUpperCase()} | {selectedConfig.total_questions || cfg.total_questions} questions | {selectedConfig.duration_minutes || cfg.duration_minutes} min | Company {selectedConfig.company_id || companyId}
            </p>
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${isDark ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-100 text-amber-800'}`}
            title="Saved AI setups are read-only after creation.">
            🔒 Read-only
          </span>
        </div>
        <p className={`mt-2 text-xs ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
          This setup has already been created, so it can't be changed.
          {typeof onRequestNew === 'function' && (
            <>
              {' '}
              <button type="button" onClick={onRequestNew}
                className="font-semibold underline underline-offset-2">Create a new setup</button>
              {' '}to make changes.
            </>
          )}
        </p>
        <SummaryGrid cfg={selectedConfig || cfg} isDark={isDark} />
        {message && <p className="mt-2 text-xs text-emerald-600">{message}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={panelCls(isDark)}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>AI interview setup</h3>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Chat setup is the source of truth for the full AI interview contract. This form preserves saved setup fields.</p>
          </div>
          <button type="button" onClick={() => window.open('/account/interview-config', '_blank', 'noopener,noreferrer')}
            className={`rounded-lg border px-3 py-2 text-xs font-semibold ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            Company policy
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {MODES.map((mode) => (
            <button key={mode.value} type="button" onClick={() => setMode(mode.value)}
              className={`rounded-lg border p-3 text-left transition ${cfg.mode === mode.value
                ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200 dark:bg-indigo-900/30 dark:ring-indigo-700'
                : isDark ? 'border-gray-600 bg-gray-700 hover:border-gray-500' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
              <span className={`block text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{mode.label}</span>
              <span className={`mt-1 block text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{mode.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={panelCls(isDark)}>
        <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Interview purpose and requirements</h4>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Interview objective</span>
            <textarea className={`${inputCls(isDark)} min-h-20`} value={cfg.setup_details?.objective || ''} onChange={(event) => patchDetails({ objective: event.target.value })} placeholder="What should this interview determine?" />
          </label>
          <label>
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Role</span>
            <input className={inputCls(isDark)} value={cfg.setup_details?.role_title || ''} onChange={(event) => patchDetails({ role_title: event.target.value })} placeholder="Area Sales Manager" />
          </label>
          <label>
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Role level</span>
            <select className={inputCls(isDark)} value={cfg.setup_details?.role_level || ''} onChange={(event) => patchDetails({ role_level: event.target.value })}>
              <option value="">Not set</option>
              {['entry', 'junior', 'mid', 'senior', 'lead', 'principal', 'manager', 'head'].map((level) => <option key={level} value={level}>{level}</option>)}
            </select>
          </label>
          <label>
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Department</span>
            <input className={inputCls(isDark)} value={cfg.setup_details?.department || ''} onChange={(event) => patchDetails({ department: event.target.value })} placeholder="Sales" />
          </label>
          <label>
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Location</span>
            <input className={inputCls(isDark)} value={cfg.setup_details?.location || ''} onChange={(event) => patchDetails({ location: event.target.value })} placeholder="Chennai, Pune" />
          </label>
          <label>
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Work mode</span>
            <select className={inputCls(isDark)} value={cfg.setup_details?.work_mode || ''} onChange={(event) => patchDetails({ work_mode: normalizeWorkMode(event.target.value) })}>
              <option value="">Not set</option>
              {['onsite', 'hybrid', 'remote', 'field'].map((mode) => <option key={mode} value={mode}>{mode}</option>)}
            </select>
          </label>
          {(isAI || isHybrid) && (
            <div className="sm:col-span-2">
              <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Priority topics</span>
              <PriorityTopicsEditor
                topics={cfg.setup_details?.priority_topics || []}
                onChange={(next) => patchDetails({ priority_topics: next })}
                onValidate={(topics) => validateTopics(companyId, { topics, sessionId: String(sourceSessionId || '') })}
                example={(cfg.setup_details?.skills || []).slice(0, 3).join(', ')}
              />
              {(cfg.setup_details?.priority_topics || []).length > 0 && (
                <label className={`mt-2 flex items-center gap-2 text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <input type="checkbox" className="rounded"
                    checked={Boolean(cfg.setup_details?.topics_only)}
                    onChange={(e) => patchDetails({ topics_only: e.target.checked })} />
                  Ask <span className="font-semibold">only these topics</span> (in this order) — skip the JD-skill
                  questions. Intro, problem-solving and behavioural questions are still included.
                </label>
              )}
            </div>
          )}
          <label>
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Skills to evaluate</span>
            <input className={inputCls(isDark)} value={(cfg.setup_details?.skills || []).join(', ')} onChange={(event) => patchDetails({ skills: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) })} placeholder="React, JavaScript, accessibility" />
          </label>
          <label>
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Difficulty</span>
            <select className={inputCls(isDark)} value={cfg.setup_details?.difficulty || ''} onChange={(event) => patchDetails({ difficulty: event.target.value })}>
              <option value="">Select difficulty</option><option value="junior">Junior</option><option value="mid">Mid</option><option value="senior">Senior</option>
            </select>
          </label>
          <label>
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Question types</span>
            <input className={inputCls(isDark)} value={(cfg.setup_details?.question_types || []).join(', ')} onChange={(event) => patchDetails({ question_types: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) })} placeholder="Technical, scenario, behavioral" />
          </label>
          <label>
            <span className={`mb-1 block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Evaluation criteria</span>
            <input className={inputCls(isDark)} value={(cfg.setup_details?.evaluation_criteria || []).join(', ')} onChange={(event) => patchDetails({ evaluation_criteria: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) })} placeholder="Technical accuracy, communication" />
          </label>
          <div className="flex flex-wrap items-center gap-4 pt-5 text-sm">
            <label className={isDark ? 'text-gray-300' : 'text-gray-700'}><input type="checkbox" className="mr-2" checked={Boolean(cfg.setup_details?.coding_or_task_required)} onChange={(event) => patchDetails({ coding_or_task_required: event.target.checked })} />Coding/task questions</label>
            <label className={isDark ? 'text-gray-300' : 'text-gray-700'}><input type="checkbox" className="mr-2" checked={Boolean(cfg.setup_details?.personalization?.resume)} onChange={(event) => patchDetails({ personalization: { ...(cfg.setup_details?.personalization || {}), resume: event.target.checked } })} />Resume based</label>
            <label className={isDark ? 'text-gray-300' : 'text-gray-700'}><input type="checkbox" className="mr-2" checked={Boolean(cfg.setup_details?.personalization?.jd)} onChange={(event) => patchDetails({ personalization: { ...(cfg.setup_details?.personalization || {}), jd: event.target.checked } })} />JD based</label>
          </div>
        </div>
      </div>

      <div className={panelCls(isDark)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className={`mb-1 block font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Interview name</span>
            <input className={inputCls(isDark)} value={cfg.name || ''} onChange={(event) => patchCfg({ name: event.target.value })} placeholder="AI interview setup 1" />
          </label>
          <label className="block text-sm">
            <span className={`mb-1 block font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Duration</span>
            <div className="flex items-center gap-2">
              <input type="number" min="15" className={inputCls(isDark)} value={cfg.duration_minutes || 20} onChange={(event) => patchCfg({ duration_minutes: Number(event.target.value) })} />
              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>minutes</span>
            </div>
          </label>
          <label className="block text-sm">
            <span className={`mb-1 block font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Coverage behavior</span>
            <select className={inputCls(isDark)} value={cfg.question_count_mode || 'fixed'} onChange={(event) => patchCfg({ question_count_mode: event.target.value })} disabled={isRecruiter}>
              <option value="coverage_minimum">Coverage-first baseline</option>
              <option value="unrestricted">Coverage-first baseline (legacy)</option>
              <option value="fixed">Fixed count (legacy)</option>
            </select>
          </label>
          {(isRecruiter || (cfg.question_count_mode || 'coverage_minimum') === 'fixed') && (
            <label className="block text-sm">
              <span className={`mb-1 block font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{isRecruiter ? 'Total questions' : 'Minimum baseline questions'}</span>
              <input type="number" min="1" className={inputCls(isDark)} value={cfg.total_questions || 10} onChange={(event) => patchCfg({ total_questions: Number(event.target.value) })} />
            </label>
          )}
        </div>
      </div>

      {(isAI || isHybrid) && (
        <div className={panelCls(isDark)}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>AI question coverage</h4>
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Use duration to create a baseline plan. Runtime can continue for unresolved required coverage while time remains.</p>
            </div>
            <span className={`rounded-full px-2 py-1 text-xs font-semibold ${categoryTotal === 0 || cfg.question_count_mode === 'unrestricted' || categoryTotal === aiQuestionTarget ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
              {categoryTotal === 0 ? 'Auto' : `${categoryTotal}/${aiQuestionTarget}`}
            </span>
          </div>
          <div className="mt-3 space-y-2">
            {(cfg.categories || []).map((item, index) => (
              <div key={`${item.category_name}-${index}`} className="grid grid-cols-[1fr_88px_auto] gap-2">
                <select className={inputCls(isDark)} value={item.category_name} onChange={(event) => updateCategory(index, { category_name: event.target.value })}>
                  {categories.map((cat) => <option key={cat.name} value={cat.name}>{labelFor(cat.name, categories)}</option>)}
                </select>
                <input type="number" min="1" className={inputCls(isDark)} value={item.question_count || 1} onChange={(event) => updateCategory(index, { question_count: Number(event.target.value) })} />
                <button type="button" onClick={() => patchCfg({ categories: (cfg.categories || []).filter((_, idx) => idx !== index) })} className="rounded-lg px-2 text-rose-500 hover:bg-rose-50">Remove</button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addCategory} className={`mt-3 rounded-lg border px-3 py-2 text-xs font-semibold ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Add category</button>
        </div>
      )}

      {(isRecruiter || isHybrid) && (
        <div className={panelCls(isDark)}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Your questions</h4>
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>These are asked exactly as provided.</p>
            </div>
            <select className={`${inputCls(isDark)} max-w-xs`} value={cfg.recruiter_question_order || 'created'} onChange={(event) => patchCfg({ recruiter_question_order: event.target.value })}>
              {RECRUITER_ORDERS.map((order) => <option key={order.value} value={order.value}>{order.label}</option>)}
            </select>
          </div>
          <div className="mt-3 space-y-3">
            {(cfg.custom_questions || []).map((question, index) => (
              <div key={index} className={`rounded-lg border p-3 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Question {index + 1}</span>
                  <button type="button" onClick={() => patchCfg({ custom_questions: (cfg.custom_questions || []).filter((_, idx) => idx !== index) })} className="text-xs font-semibold text-rose-500">Remove</button>
                </div>
                <textarea className={inputCls(isDark)} rows={2} value={question.question_text || ''} onChange={(event) => updateQuestion(index, { question_text: event.target.value })} placeholder="Type the question..." />
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <select className={inputCls(isDark)} value={question.category || categories[0]?.name || 'technical'} onChange={(event) => updateQuestion(index, { category: event.target.value })}>
                    {categories.map((cat) => <option key={cat.name} value={cat.name}>{labelFor(cat.name, categories)}</option>)}
                  </select>
                  <input className={inputCls(isDark)} value={(question.expected_skills || []).join(', ')} onChange={(event) => updateQuestion(index, { expected_skills: event.target.value.split(',').map((skill) => skill.trim()).filter(Boolean) })} placeholder="Expected skills" />
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={addQuestion} className={`mt-3 rounded-lg border px-3 py-2 text-xs font-semibold ${isDark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Add question</button>
        </div>
      )}

      <div className={panelCls(isDark)}>
        <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Silence handling</h4>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <label className="block text-sm">
            <span className={`mb-1 block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Check in after</span>
            <input type="number" min="0" className={inputCls(isDark)} value={cfg.presence_nudge_seconds ?? 60} onChange={(event) => patchCfg({ presence_nudge_seconds: Number(event.target.value) })} />
          </label>
          <label className="block text-sm">
            <span className={`mb-1 block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Check-ins</span>
            <input type="number" min="0" className={inputCls(isDark)} value={cfg.presence_nudge_count ?? 2} onChange={(event) => patchCfg({ presence_nudge_count: Number(event.target.value) })} />
          </label>
          <label className="block text-sm">
            <span className={`mb-1 block text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>End after last</span>
            <input type="number" min="0" className={inputCls(isDark)} value={cfg.presence_end_after_last_nudge_seconds ?? 60} onChange={(event) => patchCfg({ presence_end_after_last_nudge_seconds: Number(event.target.value) })} />
          </label>
        </div>
      </div>

      <div className={panelCls(isDark)}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Interview blueprint</h4>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Review the job-specific setup before saving and creating slots.</p>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${errors.length === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{errors.length === 0 ? 'Ready' : `${errors.length} missing`}</span>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ['Mode', MODES.find((item) => item.value === cfg.mode)?.label || cfg.mode],
            ['Role', cfg.setup_details?.role_title || 'Not set'],
            ['Level', cfg.setup_details?.role_level || 'Not set'],
            ['Department', cfg.setup_details?.department || 'Not set'],
            ['Location', cfg.setup_details?.location || 'Not set'],
            ['Work mode', cfg.setup_details?.work_mode || 'Not set'],
            ['Duration', `${cfg.duration_minutes || 0} minutes`],
            ['Question plan', cfg.question_count_mode === 'unrestricted' ? 'Coverage within duration' : `${cfg.total_questions || 0} questions`],
            ['Difficulty', cfg.setup_details?.difficulty || 'Not set'],
            ...((isAI || isHybrid) && (cfg.setup_details?.priority_topics || []).length
              ? [['Priority topics', (cfg.setup_details.priority_topics).join(', ')]] : []),
            ['Skills', (cfg.setup_details?.skills || []).join(', ') || 'Not set'],
            ['Question types', (cfg.setup_details?.question_types || []).join(', ') || 'Not set'],
            ['Personalization', [cfg.setup_details?.personalization?.resume ? 'Resume' : null, cfg.setup_details?.personalization?.jd ? 'JD' : null].filter(Boolean).join(' + ') || 'None'],
            ['Coding/task', cfg.setup_details?.coding_or_task_required ? 'Included' : 'Not included'],
            ['Modes', (cfg.setup_details?.interview_modes || []).join(', ') || 'Not set'],
            ['Competencies', formatCompetencies(cfg.setup_details?.required_competencies) || 'Not set'],
            ['Mandatory criteria', (cfg.setup_details?.mandatory_criteria || []).join(', ') || 'Not set'],
            ['Disqualifiers', (cfg.setup_details?.disqualifying_criteria || []).join(', ') || 'Not set'],
            ['Thresholds', cfg.threshold_config?.select || cfg.threshold_config?.maybe ? `Recommend >= ${cfg.threshold_config.select ?? 70}, Review >= ${cfg.threshold_config.maybe ?? 50}` : 'Company default'],
            ['Accommodations', formatAccommodations(cfg.setup_details?.interview_accommodations) || 'None'],
            ['Allocation', (cfg.setup_details?.question_allocation || []).map((row) => `${row.label || row.section}: ${row.count}`).join(', ') || 'Not set'],
          ].map(([label, value]) => (
            <div key={label} className={`rounded-lg p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
              <p className={`mt-1 text-xs leading-5 ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{value}</p>
            </div>
          ))}
        </div>
        {errors.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">Complete before saving</p>
            <ul className="mt-2 space-y-1 text-xs text-amber-700 dark:text-amber-300">{errors.map((item) => <li key={item}>• {item}</li>)}</ul>
          </div>
        )}
      </div>

      <div className={panelCls(isDark)}>
        {locked && (
          <p className={`mb-2 rounded-md px-3 py-2 text-xs font-semibold ${isDark ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-100 text-amber-800'}`}>
            🔒 This setup is locked — candidates have already been invited, so it can't be changed.
            {typeof onRequestNew === 'function' && (
              <> <button type="button" onClick={onRequestNew} className="underline underline-offset-2">Create a new setup</button> instead.</>
            )}
          </p>
        )}
        {errors.length > 0 && <p className="mb-2 text-sm text-red-500">{errors[0]}</p>}
        {error && <p className="mb-2 text-sm text-red-500">{error}</p>}
        {message && <p className="mb-2 text-sm text-emerald-600">{message}</p>}
        <button type="button" onClick={save} disabled={saving || errors.length > 0 || locked}
          className="w-full rounded-lg px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}>
          {saving ? 'Saving...' : locked ? 'Read-only setup' : 'Save AI interview setup'}
        </button>
      </div>
    </div>
  );
};

export default AIInterviewConfigSelector;
